import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import styled from "styled-components";
import { registrationSchema } from "../utils/validationSchemas";
import { registerUser, getProvinces, loginUser, generateCertUrl } from "../services/api";
import sidebarLogo from "../assets/sidebar_logo.svg";
import headerLogo from "../assets/header_logo.svg";
import { useAuth } from "../context/AuthContext";
import { FONTS, COLORS, BREAKPOINTS, SHADOWS } from "../utils/styleConstants";
import Footer from "../components/Footer";


// Styled components
const TooltipArrow = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
})`
  position: absolute;
  top: -15px;
  left: 113%;
  background: linear-gradient(90deg, #6b8dd6 7%, ${COLORS.SECONDARY} 20%);
  padding: 2rem;
  color: ${COLORS.PRIMARY};
  font-size: 0.875rem;
  width: 100rem;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.3s, visibility 0.3s;
  font-family: ${FONTS.PRIMARY};
  font-weight: bold;
  z-index: 20;
  height: 7rem;
  display: flex;
  align-items: center;
  clip-path: polygon(40px 0, 100% 0, 100% 100%, 40px 100%, 0 50%);
  padding-right: 29rem;
  padding-left: 3.5rem;

  span {
    text-decoration: underline;
    max-width: 33rem;
    font-size: clamp(0.75rem, 1vw, 0.875rem);
    line-height: 1.4;
    margin-left: 40px;
    color: white;

    @media (max-width: 1200px) {
      max-width: 25rem;
    }

    @media (max-width: 1000px) {
      max-width: 15rem;
    }
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}) {
    padding-right: 0rem;
    padding-left: 3rem;
  }

  @media (max-width: ${BREAKPOINTS.DESKTOP}) {
    padding-right: 0rem;
    padding-left: 3rem;
  }

  @media (max-width: ${BREAKPOINTS.LAPTOP}) {
    padding-right: 0rem;
    padding-left: 3rem;
    left: 125%;
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    display: none;
  }
`;

const PopupInfo = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
})`
  display: none;
  position: absolute;
  top: calc(100% + 15px);
  left: 0;
  right: 0;
  background-color: rgb(237, 242, 247);
  padding: 1rem;
  border-radius: 8px 2px 8px 8px;
  font-family: ${FONTS.SECONDARY};
  font-size: 0.875rem;
  line-height: 1.4;
  color: #080d44;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: opacity 0.3s, visibility 0.3s;

  &::before {
    content: "";
    position: absolute;
    top: -8px;
    right: 1px;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid rgb(237, 242, 247);
  }

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    display: block;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: ${COLORS.SECONDARY};
  display: flex;
  box-shadow: ${SHADOWS.DEFAULT};
  overflow: hidden;
  height: 100vh;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const Sidebar = styled.div`
  width: 120px;
  background-color: var(--secondary-color);
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;

  @media (max-width: 768px) {
    width: 100%;
    height: 70px;
    padding: 1rem;
    flex-direction: row;
    justify-content: flex-start;
    background-color: #080d44;
  }
`;

const SidebarLogo = styled.img`
  width: 40px;
  height: auto;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    width: 30px;
    margin-bottom: 0;
    margin-left: 1rem;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: linear-gradient(157deg, #ffeee7 27%, #c1d6ef 70%);
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: linear-gradient(157deg, #ffeee7 40%, #c1d6ef 100%);
  }
`;

const FormHeader = styled.div`
  margin-bottom: 1rem;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
  align-items: start;
  flex-shrink: 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    margin-bottom: 2rem;
  }
`;

const HeaderLogo = styled.img`
  height: 2.5rem;
  width: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormTitle = styled.h1`
  color: var(--primary-color);
  font-size: 2.5rem;
  font-weight: 600;
  font-family: "AtHauss", "Avenir Next", sans-serif;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    color: #ff7f50;
  }
`;

const FormSubtitle = styled.p`
  color: var(--accent-color);
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  font-weight: 400;
  font-style: bold;
  margin: 0;
  max-width: 37%;
  line-height: 1.4;
  white-space: pre-line;
  min-height: 2.8em;

  @media (max-width: 1200px) {
    max-width: 100%;
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    line-height: 1.3;
    min-height: auto;
    margin-bottom: 1rem;
    color: #ff7f50;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 7rem;
  margin-bottom: 0.5rem;
  flex: 1;
  align-content: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-gap: 0;
    margin-bottom: 1rem;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const Label = styled.label.withConfig({
  shouldForwardProp: (prop) => !['hideOnMobile', 'required'].includes(prop),
})`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: var(--secondary-color);
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-weight: 500;
  position: relative;

  ${props => props.required && `
    &::after {
      content: "*";
      color: #ff4444;
      margin-left: 2px;
      font-size: 1rem;
    }
  `}

  @media (max-width: 768px) {
    color: #080d44;
    font-size: 0.9rem;
    display: ${(props) => (props.hideOnMobile ? "none" : "block")};
  }
`;

const Input = styled.input`
  padding: 0.25rem 0;
  border: none;
  font-size: 1rem;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  margin-right: 1rem;

  /* Animaciones para detectar autocompletado */
  &:-webkit-autofill {
    animation: onAutoFillStart 0s forwards;
  }

  &:not(:-webkit-autofill) {
    animation: onAutoFillCancel 0s forwards;
  }

  @keyframes onAutoFillStart {
    from { /* empty */ }
    to { /* empty */ }
  }

  @keyframes onAutoFillCancel {
    from { /* empty */ }
    to { /* empty */ }
  }

  @media (max-width: 768px) {
    text-align: left;
    margin-right: 0;
    font-size: 0.9rem;
  }
`;

const PasswordField = styled.div`
  position: relative;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PasswordInput = styled(Input)`
  width: 100%;
  padding-right: 40px;
  border: none;
  margin-right: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    margin-right: 0;
  }
`;

const SearchInput = styled(Input)`
  width: 100%;
  border-bottom: none;
  padding: 0.75rem 0;
  margin-right: 1rem;
`;

const Select = styled.select`
  padding: 0.25rem 0;
  border: none;
  font-size: 1rem;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  outline: none;
  background-color: transparent;
  width: 100%;
  text-align: right;
  margin-right: 1rem;
  cursor: pointer;

  @media (max-width: 768px) {
    text-align: left;
    margin-right: 0;
    font-size: 0.9rem;
    width: 100%;
  }
`;

const ErrorIconSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 7V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="16" r="1" fill="currentColor"/>
  </svg>
);

const ErrorMessage = styled.div`
  color: var(--accent-color);
  font-size: 0.75rem;
  position: absolute;
  bottom: 0.3rem;
  right: 1rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    color: var(--accent-color);
  }

  @media (max-width: 768px) {
    bottom: -1.5rem;
    right: 0rem;
  }
`;

const HorizontalField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.5rem;
  position: relative;
  height: 3rem;

  .input-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    height: 2.5rem;
  }

  ${Label} {
    margin-bottom: 0;
    flex: 1;
  }

  ${Select}, ${Input}, ${PasswordInput}, ${SearchInput} {
    flex: 1;
    border-bottom: none;
  }

  ${PasswordField} {
    flex: 1;
  }

  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ccc;

  &:focus-within {
    border-bottom-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    height: auto;
    margin-bottom: 1.5rem;

    .input-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
      height: auto;
    }

    ${Label} {
      width: 100%;
      margin-bottom: 0.5rem;
    }

    ${Select}, ${Input}, ${PasswordInput}, ${SearchInput} {
      width: 100%;
      text-align: left;
      padding-left: 0;
    }

    border-bottom: 1px solid #bad1e6;
  }
`;

const EyeIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    right: 0;
  }
`;

const EyeIconSvg = ({ isVisible }) =>
  isVisible ? (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
        fill="currentColor"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
        fill="currentColor"
      />
    </svg>
  );

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  height: 3rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #ccc;

  &:focus-within {
    border-bottom-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const ToggleLabel = styled.div`
  font-size: 1rem;
  color: var(--secondary-color);
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-weight: 500;
  position: relative;

  ${props => props.required && `
    &::after {
      content: "*";
      color: #ff4444;
      margin-left: 2px;
      font-size: 1rem;
    }
  `}

  @media (max-width: 768px) {
    color: #080d44;
    font-size: 0.9rem;
  }
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 40px;
  height: 20px;
  background-color: ${(props) =>
    props.checked ? "var(--secondary-color)" : "#ccc"};
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.3s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-right: 1rem;

  &::after {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: white;
    top: 2px;
    left: ${(props) => (props.checked ? "22px" : "2px")};
    transition: left 0.3s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    background-color: ${(props) => (props.checked ? "#080d44" : "#ccc")};
  }
`;

const NoLabel = styled.span`
  position: absolute;
  right: -30px;
  font-size: 0.9rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 2rem;
  }
`;

const SubmitButton = styled.button`
  background-color: #ff7f50;
  color: white;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-weight: 600;
  border: none;
  border-radius: 2rem;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #e06542;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 90%;
    padding: 1rem;
    border-radius: 2.5rem;
  }
`;

const RequiredMessage = styled.p`
  color: var(--text-color);
  font-size: 0.875rem;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-weight: 300;
  margin-top: 0.5rem;
  text-align: left;
  margin-bottom: 0.5rem;
  text-decoration: underline;
  flex-shrink: 0;

  @media (max-width: 768px) {
    text-align: center;
    margin-top: 0;
    font-size: 0.75rem;
  }
`;

const InfoMessage = styled.div`
  padding: 0.75rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  background-color: #fff8f6;
  border: 1px solid #ffcdb7;
  color: #b54800;
  font-size: 0.9rem;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  max-width: calc(50% - 3.5rem);
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    text-align: center;
    max-width: 100%;
  }
`;

const TypeDropdownContainer = styled.div`
  position: relative;
  flex: 1;
  margin-right: 1rem;
  display: flex;
  align-items: center;

  ${TooltipArrow} {
    pointer-events: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 15px;
  }
`;

const InfoIcon = styled.div`
  display: none;
  margin-left: 0.5rem;
  cursor: pointer;
  color: rgb(149 186 228);
  outline: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const InfoIconSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
      fill="currentColor"
    />
  </svg>
);

const TypeDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
  z-index: 10;
  box-shadow: var(--box-shadow);
`;

const TypeOption = styled.div`
  padding: 0.75rem;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const ProvinceDropdown = styled.div`
  position: relative;
  flex: 1;
  margin-right: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 15px;
  }
`;

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
  z-index: 10;
  list-style: none;
  margin: 0;
  padding: 0;
  box-shadow: var(--box-shadow);
`;

const DropdownItem = styled.li`
  padding: 0.5rem 1rem;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const LawyerSection = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;

  ${ButtonContainer} {
    margin-top: 1rem;
    padding-bottom: 1rem;
  }

  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

const FormError = styled(ErrorMessage)`
  position: static;
  margin-bottom: 1rem;
`;

const RadioGroup = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 768px) {
    align-items: flex-start;
    margin-bottom: 0.5rem;
    width: 100%;
    padding: 1rem 0;
  }
`;

const CheckboxGroup = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 768px) {
    align-items: flex-start;
    margin-bottom: 1.5rem;
    width: 100%;
  }
`;

const RadioLabel = styled.label`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
  cursor: pointer;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-size: 0.9rem;
  opacity: ${(props) => (props.checked ? 1 : 0.7)};
  gap: 0.5rem;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
    font-size: 0.8rem;
    color: #080d44;
    margin-bottom: 0.8rem;
    flex-direction: row-reverse;
  }
`;

const RadioText = styled.span`
  flex: 1;
  text-align: right;
  line-height: 1.4;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const RadioInput = styled.input`
  margin-left: 0.5rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-right: 1rem;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
  cursor: pointer;
  font-family: "WorkSans", "Avenir Next", sans-serif;
  font-size: 0.9rem;
  opacity: ${(props) => (props.checked ? 1 : 0.7)};
  gap: 0.5rem;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
    font-size: 0.8rem;
    color: #080d44;
    margin-bottom: 0.8rem;
    flex-direction: row-reverse;
  }
`;

const CheckboxText = styled.span`
  flex: 1;
  text-align: right;
  line-height: 1.4;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const CheckboxInput = styled.input`
  margin-left: 0.5rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-right: 1rem;
  }
`;

const PrivacyLink = styled.a`
  color: var(--primary-color);
  text-decoration: underline;
  margin-left: 0.25rem;

  @media (max-width: 768px) {
    color: #080d44;
  }
`;

// Modal de error (copiado y adaptado del Footer)
const ErrorModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ErrorModalContent = styled.div`
  background-color: #fff;
  padding: 2rem;
  border-radius: 1rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
`;

const ErrorModalTitle = styled.h2`
  color: ${COLORS.SECONDARY};
  font-size: 1.2rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0;
`;

const ErrorModalCloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${COLORS.SECONDARY};
`;

const ErrorModalMessage = styled.div`
  color: ${COLORS.SECONDARY};
  font-size: 1rem;
  text-align: left;
  width: 100%;
  margin-bottom: 1rem;
`;

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [provinces, setProvinces] = useState([]);
  const [showProvinceDropdown, setShowProvinceDropdown] = useState(false);
  const [provinceFilter, setProvinceFilter] = useState("");
  const [filteredProvinces, setFilteredProvinces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showIdTooltip, setShowIdTooltip] = useState(false);
  const [privacyOption, setPrivacyOption] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const provinceDropdownRef = useRef(null);
  const infoIconRef = useRef(null);
  const mobilePhoneRef = useRef(null);
  const [showErrorModal, setShowErrorModal] = useState(false);


  useEffect(() => {
    // Fetch provinces on component mount
    const fetchProvinces = async () => {
      try {
        const data = await getProvinces();
        setProvinces(data);
        setFilteredProvinces(data);
      } catch (error) {
        console.error("Error fetching provinces:", error);
        setError("Error cargando las provincias");
      }
    };

    fetchProvinces();
  }, []);

  useEffect(() => {
    // Filter provinces based on search input
    if (provinces.length > 0) {
      const filtered = provinces.filter((province) =>
        province.name.toLowerCase().includes(provinceFilter.toLowerCase())
      );
      setFilteredProvinces(filtered);
    }
  }, [provinceFilter, provinces]);

  useEffect(() => {
    // Handle click outside province dropdown
    const handleClickOutside = (event) => {
      if (
        provinceDropdownRef.current &&
        !provinceDropdownRef.current.contains(event.target)
      ) {
        setShowProvinceDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoIconRef.current && !infoIconRef.current.contains(event.target)) {
        setShowIdTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);



  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      second_last_name: "",
      email: "",
      id_type: "",
      id_number: "",
      password: "",
      confirm_password: "",
      mobile_phone: "",
      landline: "",
      is_lawyer: false,
      lawyer_membership_number: "",
      province_id: "",
      province_name: "",
      accepts_emails: false,
      // NUEVO: sociedad
      acts_on_behalf_of_company: false,
      company_name: "",
      company_tax_id: "",
    },
    validationSchema: registrationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      try {
        // Preparar datos del formulario
        const userData = {
          first_name: values.first_name,
          last_name: values.last_name,
          second_last_name: values.second_last_name,
          email: values.email,
          id_type: values.id_type,
          id_number: values.id_number,
          password: values.password,
          mobile_phone: values.mobile_phone,
          landline: values.landline,
          is_lawyer: values.is_lawyer,
          lawyer_membership_number: values.is_lawyer
            ? values.lawyer_membership_number
            : null,
          province_id: values.is_lawyer ? values.province_id : null,
          accepts_emails: values.accepts_emails,
          // Sociedad
          acts_on_behalf_of_company: !!values.acts_on_behalf_of_company,
          company_name: values.acts_on_behalf_of_company ? values.company_name : null,
          company_tax_id: values.acts_on_behalf_of_company ? String(values.company_tax_id).toUpperCase() : null,
        };

        // Generar URL de certificado
        const { certificateUrl } = await generateCertUrl();

        // Hacer POST directo a la URL con certificado SSL
        // El navegador automáticamente solicitará el certificado
        const response = await fetch(certificateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Error en el registro');
        }

        const result = await response.json();

        // Si hay token, guardarlo
        if (result.token) {
          localStorage.setItem("token", result.token);
          // Configurar token en axios para futuras peticiones
          const { default: api } = await import('../services/api');
          api.defaults.headers.common['Authorization'] = `Bearer ${result.token}`;
        }

        // Actualizar estado de autenticación
        login();

        // Registro exitoso - navegar a ofertas
        navigate("/offers");
      } catch (error) {
        console.error('Error en registro:', error);
        // Manejar errores de registro en español
        if (error.message.includes('409')) {
          setError("No se pudo completar el registro. Verifica los datos ingresados.");
        } else if (error.message.includes('400')) {
          setError("Datos inválidos");
        } else if (error.message.includes('certificado')) {
          setError("Error con el certificado digital: " + error.message);
        } else {
          setError("Error al guardar el usuario: " + error.message);
        }
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    },
  });

  // Detectar cambios en el campo de teléfono causados por autocompletado
  useEffect(() => {
    if (!mobilePhoneRef.current || !formik) return;

    const phoneInput = mobilePhoneRef.current;
    let lastValue = phoneInput.value;

    const checkForAutoFill = () => {
      const currentValue = phoneInput.value;
      if (currentValue !== lastValue && currentValue !== formik.values.mobile_phone) {
        formik.setFieldValue('mobile_phone', currentValue);
        formik.setFieldTouched('mobile_phone', true);
        lastValue = currentValue;
      }
    };

    // Usar un intervalo para detectar cambios no capturados
    const interval = setInterval(checkForAutoFill, 100);

    // También escuchar el evento animationstart que se dispara con autocompletado
    const handleAnimationStart = (e) => {
      if (e.animationName === 'onAutoFillStart' || e.animationName === 'onAutoFillCancel') {
        setTimeout(checkForAutoFill, 10);
      }
    };

    phoneInput.addEventListener('animationstart', handleAnimationStart);

    return () => {
      clearInterval(interval);
      phoneInput.removeEventListener('animationstart', handleAnimationStart);
    };
  }, [formik]);

  const handleProvinceClick = (province) => {
    formik.setFieldValue("province_id", province.id);
    formik.setFieldValue("province_name", province.name);
    setShowProvinceDropdown(false);
  };

  const getSelectedProvinceName = () => {
    if (formik.values.province_name) {
      return formik.values.province_name;
    }

    if (formik.values.province_id && provinces.length > 0) {
      const province = provinces.find(
        (p) => p.id === formik.values.province_id
      );
      return province ? province.name : "";
    }

    return "";
  };

  const togglePasswordVisibility = (field) => {
    if (field === "password") {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Handle privacy option selection
  const handlePrivacyOptionChange = (option) => {
    setPrivacyOption(option);
    formik.setFieldValue("accepts_emails", option === "accept_emails");
  };

  // Handle terms acceptance
  const handleTermsAcceptanceChange = (e) => {
    setTermsAccepted(e.target.checked);
  };

  // Handler personalizado para el campo de teléfono
  const handleMobilePhoneChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue('mobile_phone', value);
    formik.setFieldTouched('mobile_phone', true);
  };



  // Check if form can be submitted
  const canSubmit = privacyOption !== "" && termsAccepted && formik.isValid;

  return (
    <FormContainer>
      <Sidebar>
        <SidebarLogo src={sidebarLogo} alt="Logo" />
      </Sidebar>
      <ContentArea>
        <FormHeader>
          <HeaderLogo src={headerLogo} alt="Header Logo" />
          <HeaderContent>
            <FormTitle>Crea tu cuenta</FormTitle>
            <FormSubtitle>
              y empieza a enviar, negociar o validar ofertas vinculantes de
              forma automatizada de principio a fin
            </FormSubtitle>
          </HeaderContent>
        </FormHeader>

        <form onSubmit={formik.handleSubmit}>
          <FormGrid>
            {/* Columna izquierda: Campos principales */}
            <div>
              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="first_name" required>Nombre</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.first_name}
                    error={
                      formik.touched.first_name && formik.errors.first_name
                    }
                  />
                </div>
                {formik.touched.first_name && formik.errors.first_name && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.first_name}
                  </ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="last_name" required>Apellidos</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.last_name}
                    error={formik.touched.last_name && formik.errors.last_name}
                  />
                </div>
                {formik.touched.last_name && formik.errors.last_name && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.last_name}
                  </ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="email" required>Mail</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    error={formik.touched.email && formik.errors.email}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.email}
                  </ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="mobile_phone" required>Teléfono móvil</Label>
                  <Input
                    ref={mobilePhoneRef}
                    id="mobile_phone"
                    name="mobile_phone"
                    type="text"
                    onChange={handleMobilePhoneChange}
                    onInput={handleMobilePhoneChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.mobile_phone}
                    error={
                      formik.touched.mobile_phone && formik.errors.mobile_phone
                    }
                  />
                </div>
                {formik.touched.mobile_phone && formik.errors.mobile_phone && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.mobile_phone}
                  </ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="id_type" hideOnMobile required>Documento de identidad</Label>
                  <TypeDropdownContainer>
                    <Select
                      id="id_type"
                      name="id_type"
                      onChange={formik.handleChange}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        setShowIdTooltip(false);
                      }}
                      onFocus={() => setShowIdTooltip(true)}
                      value={formik.values.id_type}
                      error={formik.touched.id_type && formik.errors.id_type}
                    >
                      <option value="" disabled>
                        Tipo de documento
                      </option>
                      <option value="NIF">DNI</option>
                      <option value="NIE">NIE</option>
                      {/* <option value="CIF">CIF</option> */}
                      {/* <option value="Passport">Pasaporte</option> */}
                    </Select>
                    <InfoIcon
                      ref={infoIconRef}
                      tabIndex={0}
                      onFocus={() => setShowIdTooltip(true)}
                      onBlur={() => setShowIdTooltip(false)}
                    >
                      <InfoIconSvg />
                    </InfoIcon>
                    <TooltipArrow visible={showIdTooltip}>
                      <span>
                        El documento de identidad que uses para iniciar sesión debe ser el mismo que el de tu certificado digital, con el que podrás firmar las Ofertas Vinculantes.
                        El certificado podrá ser personal o de una sociedad, siempre que sea con un DNI/NIE del representante de esa sociedad
                      </span>
                    </TooltipArrow>
                    <PopupInfo visible={showIdTooltip}>
                      El documento de identidad que uses para iniciar sesión debe ser el mismo que el de tu certificado digital, con el que podrás firmar las Ofertas Vinculantes.
                      El certificado podrá ser personal o de una sociedad, siempre que sea con un DNI/NIE del representante de esa sociedad
                    </PopupInfo>
                  </TypeDropdownContainer>
                </div>
                {formik.touched.id_type && formik.errors.id_type && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.id_type}
                  </ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="id_number" required>Número de documento de identidad</Label>
                  <Input
                    id="id_number"
                    name="id_number"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.id_number}
                    error={formik.touched.id_number && formik.errors.id_number}
                  />
                </div>
                {formik.touched.id_number && formik.errors.id_number && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.id_number}
                  </ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="password" required>Contraseña</Label>
                  <PasswordField>
                    <PasswordInput
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      error={formik.touched.password && formik.errors.password}
                    />
                    <EyeIcon
                      onClick={() => togglePasswordVisibility("password")}
                    >
                      <EyeIconSvg isVisible={!showPassword} />
                    </EyeIcon>
                  </PasswordField>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <ErrorMessage>
                    <ErrorIconSvg />
                    {formik.errors.password}
                  </ErrorMessage>
                )}
              </HorizontalField>

              <HorizontalField>
                <div className="input-row">
                  <Label htmlFor="confirm_password" required>Repetir contraseña</Label>
                  <PasswordField>
                    <PasswordInput
                      id="confirm_password"
                      name="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.confirm_password}
                      error={
                        formik.touched.confirm_password &&
                        formik.errors.confirm_password
                      }
                    />
                    <EyeIcon
                      onClick={() => togglePasswordVisibility("confirm")}
                    >
                      <EyeIconSvg isVisible={!showConfirmPassword} />
                    </EyeIcon>
                  </PasswordField>
                </div>
                {formik.touched.confirm_password &&
                  formik.errors.confirm_password && (
                    <ErrorMessage>
                      <ErrorIconSvg />
                      {formik.errors.confirm_password}
                    </ErrorMessage>
                  )}
              </HorizontalField>
            </div>

            {/* Columna derecha: ¿Eres Abogado? y campos relacionados */}
            <div>
              <ToggleContainer>
                <ToggleLabel htmlFor="is_lawyer" required>¿Eres Abogado?</ToggleLabel>
                <ToggleSwitch
                  checked={formik.values.is_lawyer}
                  onClick={() =>
                    formik.setFieldValue("is_lawyer", !formik.values.is_lawyer)
                  }
                >
                  {formik.values.is_lawyer ? <NoLabel>SI</NoLabel> : <NoLabel>NO</NoLabel>}
                </ToggleSwitch>
              </ToggleContainer>

              <LawyerSection>
                {formik.values.is_lawyer && (
                  <>
                    <HorizontalField>
                      <div className="input-row">
                        <Label htmlFor="lawyer_membership_number" required>Número de colegiado</Label>
                        <Input
                          id="lawyer_membership_number"
                          name="lawyer_membership_number"
                          type="text"
                          onChange={(e) => {
                            formik.handleChange(e);
                            formik.setFieldTouched('lawyer_membership_number', true, false);
                          }}
                          onBlur={formik.handleBlur}
                          value={formik.values.lawyer_membership_number}
                          error={
                            formik.touched.lawyer_membership_number &&
                            formik.errors.lawyer_membership_number
                          }
                        />
                      </div>
                      {formik.touched.lawyer_membership_number &&
                        formik.errors.lawyer_membership_number && (
                          <ErrorMessage>
                            <ErrorIconSvg />
                            {formik.errors.lawyer_membership_number}
                          </ErrorMessage>
                        )}
                    </HorizontalField>

                    <HorizontalField>
                      <div className="input-row">
                        <Label htmlFor="province_id" hideOnMobile required>Indica la localidad del colegio</Label>
                        <ProvinceDropdown ref={provinceDropdownRef}>
                          <SearchInput
                            type="text"
                            placeholder="Buscar provincia..."
                            onClick={() => setShowProvinceDropdown(true)}
                            onChange={(e) => setProvinceFilter(e.target.value)}
                            value={
                              showProvinceDropdown
                                ? provinceFilter
                                : getSelectedProvinceName()
                            }
                            onFocus={() => setShowProvinceDropdown(true)}
                            onBlur={() => {
                              // No cerramos inmediatamente para permitir que se haga clic en una opción
                              setTimeout(() => {
                                if (
                                  !provinceDropdownRef.current?.contains(
                                    document.activeElement
                                  )
                                ) {
                                  setShowProvinceDropdown(false);
                                }
                              }, 200);
                            }}
                            error={
                              formik.touched.province_id &&
                              formik.errors.province_id
                            }
                          />
                          {showProvinceDropdown && (
                            <DropdownList>
                              {filteredProvinces.map((province) => (
                                <DropdownItem
                                  key={province.id}
                                  onClick={() => handleProvinceClick(province)}
                                >
                                  {province.name}
                                </DropdownItem>
                              ))}
                            </DropdownList>
                          )}
                        </ProvinceDropdown>
                      </div>
                      {formik.touched.province_id &&
                        formik.errors.province_id && (
                          <ErrorMessage>
                            <ErrorIconSvg />
                            {formik.errors.province_id}
                          </ErrorMessage>
                        )}
                    </HorizontalField>
                  </>
                )}

                {/* Toggle sociedad (debajo de localidad) */}
                <ToggleContainer>
                  <ToggleLabel htmlFor="acts_on_behalf_of_company" required>¿Actúas en nombre de una sociedad?</ToggleLabel>
                  <ToggleSwitch
                    checked={formik.values.acts_on_behalf_of_company}
                    onClick={() =>
                      formik.setFieldValue('acts_on_behalf_of_company', !formik.values.acts_on_behalf_of_company)
                    }
                  >
                    {formik.values.acts_on_behalf_of_company ? <NoLabel>SI</NoLabel> : <NoLabel>NO</NoLabel>}
                  </ToggleSwitch>
                </ToggleContainer>

                {formik.values.acts_on_behalf_of_company && (
                  <>
                    <HorizontalField>
                      <div className="input-row">
                        <Label htmlFor="company_name" required>Razón social</Label>
                        <Input
                          id="company_name"
                          name="company_name"
                          type="text"
                          placeholder="Empresa, S.L"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.company_name}
                          error={formik.touched.company_name && formik.errors.company_name}
                        />
                      </div>
                      {formik.touched.company_name && formik.errors.company_name && (
                        <ErrorMessage>
                          <ErrorIconSvg />
                          {formik.errors.company_name}
                        </ErrorMessage>
                      )}
                    </HorizontalField>

                    <HorizontalField>
                      <div className="input-row">
                        <Label htmlFor="company_tax_id" required>NIF / CIF</Label>
                        <Input
                          id="company_tax_id"
                          name="company_tax_id"
                          type="text"
                          placeholder="B1234567C"
                          onChange={(e)=>{
                            const upper = e.target.value.toUpperCase();
                            formik.setFieldValue('company_tax_id', upper);
                          }}
                          onBlur={formik.handleBlur}
                          value={formik.values.company_tax_id}
                          error={formik.touched.company_tax_id && formik.errors.company_tax_id}
                        />
                      </div>
                      {formik.touched.company_tax_id && formik.errors.company_tax_id && (
                        <ErrorMessage>
                          <ErrorIconSvg />
                          {formik.errors.company_tax_id}
                        </ErrorMessage>
                      )}
                    </HorizontalField>
                  </>
                )}

                {/* Privacy Policy Options */}
                <RadioGroup>
                  <RadioLabel checked={privacyOption === "accept_emails"}>
                    <RadioText>
                      He leído la{" "}
                      <PrivacyLink
                        href="https://www.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Política de privacidad
                      </PrivacyLink>{" "}
                      y acepto recibir mails
                    </RadioText>
                    <RadioInput
                      type="radio"
                      name="privacyPolicy"
                      value="accept_emails"
                      checked={privacyOption === "accept_emails"}
                      onChange={() =>
                        handlePrivacyOptionChange("accept_emails")
                      }
                    />
                  </RadioLabel>
                  <RadioLabel checked={privacyOption === "reject_emails"}>
                    <RadioText>
                      He leído la{" "}
                      <PrivacyLink
                        href="https://www.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Política de privacidad
                      </PrivacyLink>{" "}
                      y NO acepto recibir mails
                    </RadioText>
                    <RadioInput
                      type="radio"
                      name="privacyPolicy"
                      value="reject_emails"
                      checked={privacyOption === "reject_emails"}
                      onChange={() =>
                        handlePrivacyOptionChange("reject_emails")
                      }
                    />
                  </RadioLabel>
                </RadioGroup>

                {/* Terms and Conditions */}
                <CheckboxGroup>
                  <CheckboxLabel checked={termsAccepted}>
                    <CheckboxText>
                      He leído y acepto las{" "}
                      <PrivacyLink
                        href="https://ofertasvinculantes.es/condiciones-generales"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Condiciones de contratación
                      </PrivacyLink>
                    </CheckboxText>
                    <CheckboxInput
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={handleTermsAcceptanceChange}
                    />
                  </CheckboxLabel>
                </CheckboxGroup>

                <ButtonContainer>
                  <SubmitButton type="submit" disabled={loading || !canSubmit}>
                    Aceptar
                  </SubmitButton>
                </ButtonContainer>
              </LawyerSection>
            </div>
          </FormGrid>

          <InfoMessage>
            Recuerda que para formular Ofertas Vinculantes cuya cuantía objeto de la controversia supere los 2.000€, la Ley exige la asistencia letrada
          </InfoMessage>

          <RequiredMessage>Todos los campos son obligatorios</RequiredMessage>
        </form>

        {/* Modal de error */}
        {showErrorModal && error && (
          <ErrorModalOverlay onClick={() => setShowErrorModal(false)}>
            <ErrorModalContent onClick={(e) => e.stopPropagation()}>
              <ErrorModalHeader>
                <ErrorModalTitle>Error</ErrorModalTitle>
                <ErrorModalCloseButton onClick={() => setShowErrorModal(false)}>
                  ×
                </ErrorModalCloseButton>
              </ErrorModalHeader>
              <ErrorModalMessage>{error}</ErrorModalMessage>
            </ErrorModalContent>
          </ErrorModalOverlay>
        )}
        <Footer />
      </ContentArea>
    </FormContainer>
  );
};

export default RegistrationForm;
